'use client';

import {isError} from '@epic-mw/error-types';
import useSWRImmutable from 'swr/immutable';

import {API, getApiPath} from '@/lib/utils/api.utils';
import {useAccountCountry, useAccountStore} from '../account';
import {isBrowser} from '@/lib/utils/page.utils';

export interface GeoRes {
	country?: string;
	countryName?: string;
	countryRegion?: string;
	countryRegionName?: string;
}

export const getGeo = async (): Promise<GeoRes> => {
	try {
		const response = await API.get<GeoRes>(`${getApiPath()}/geo`);
		return response?.data;
	} catch (err) {
		console.error('geo() failed to load', isError(err) && err.message);
		throw err;
	}
};

export const useGeo = (
	shouldRun = true
): {
	geo?: GeoRes;
	isLoading: boolean;
	error?: string;
} => {
	const {data, error} = useSWRImmutable(shouldRun ? 'geo' : null, getGeo, {
		errorRetryCount: 1
	});

	return {
		geo: data,
		isLoading: !data && !error,
		error
	};
};

export const useGeoCountry = (): string => {
	try {
		return isBrowser() ? window._epicTrackingCountryCode : 'US';
	} catch {
		return 'US';
	}
};

export const useCountry = () => {
	const {isLoading: accountLoading} = useAccountStore();
	const accountCountry = useAccountCountry();
	const geoCountry = useGeoCountry();

	// lets wait until after loading is complete to return actual country
	if (accountLoading) {
		return '';
	}

	return accountCountry || geoCountry || 'US';
};
